// usePersonOptions.js
import { useState, useEffect } from 'react';

// 缓存选项数据
let cachedOptions = null;
let isLoading = false;
let loadError = null;
// 等待加载的回调队列
const pendingPromises = [];

/**
 * 获取人物筛选选项的Hook，带全局缓存
 * @returns {Object} { options, isLoading, error, refreshOptions }
 */
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export const usePersonOptions = () => {
  const [options, setOptions] = useState(cachedOptions);
  const [loading, setLoading] = useState(!cachedOptions && isLoading);
  const [error, setError] = useState(loadError);

  useEffect(() => {
    const fetchOptions = async () => {
      // 如果已经有缓存数据，直接使用
      if (cachedOptions) {
        setOptions(cachedOptions);
        setLoading(false);
        return;
      }
      
      // 如果正在加载中，等待加载完成
      if (isLoading) {
        const promise = new Promise((resolve, reject) => {
          pendingPromises.push({ resolve, reject });
        });
        
        promise
          .then(data => {
            setOptions(data);
            setLoading(false);
            setError(null);
          })
          .catch(err => {
            setError(err);
            setLoading(false);
          });
        
        return;
      }
      
      // 开始新的加载过程
      isLoading = true;
      setLoading(true);
      
      console.log('开始从后端获取选项数据...');
      try {
        const response = await fetch('${API_BASE_URL}/api/options/person');
        
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        
        const backendData = await response.json();
        console.log('成功从后端获取原始选项数据:', backendData);
    
        const formattedOptions = transformOptionsForUI(backendData);
        console.log('格式化后的选项数据:', formattedOptions);
        
        // 更新缓存
        cachedOptions = formattedOptions;
        loadError = null;
        
        // 更新组件状态
        setOptions(formattedOptions);
        setError(null);
        
        // 通知所有等待的Promise
        pendingPromises.forEach(p => p.resolve(formattedOptions));
        pendingPromises.length = 0;
      } catch (err) {
        console.error('Error fetching person options:', err);
        
        // 记录错误
        loadError = err.message;
        
        // 更新组件状态
        setError(err.message);
        
        // 通知所有等待的Promise
        pendingPromises.forEach(p => p.reject(err));
        pendingPromises.length = 0;
        
        // 使用空选项
        const emptyOptions = getEmptyOptions();
        setOptions(emptyOptions);
      } finally {
        isLoading = false;
        setLoading(false);
      }
    };
    
    fetchOptions();
  }, []);
  
  // 提供手动刷新选项的函数
  const refreshOptions = async () => {
    // 清除缓存
    cachedOptions = null;
    loadError = null;
    isLoading = false;
    pendingPromises.length = 0;
    
    // 重新加载
    setLoading(true);
    
    try {
      const response = await fetch('${API_BASE_URL}/api/options/person');
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const backendData = await response.json();
      const formattedOptions = transformOptionsForUI(backendData);
      
      // 更新缓存
      cachedOptions = formattedOptions;
      
      // 更新组件状态
      setOptions(formattedOptions);
      setError(null);
    } catch (err) {
      console.error('Error refreshing person options:', err);
      setError(err.message);
      loadError = err.message;
      
      // 使用空选项
      const emptyOptions = getEmptyOptions();
      setOptions(emptyOptions);
    } finally {
      setLoading(false);
    }
  };
  
  return { options, isLoading: loading, error, refreshOptions };
};

/**
 * 获取空选项结构
 * @returns {Object} 仅包含'none'选项的空结构
 */
function getEmptyOptions() {
  return {
    hat: {
      color: [{ value: 'none', label: 'Color: None' }]
    },
    hair: {
      color: [{ value: 'none', label: 'Color: None' }]
    },
    clothing: {
      color: [{ value: 'none', label: 'Color: None' }],
      type: [{ value: 'none', label: 'Type: None' }]
    },
    skin: {
      color: [{ value: 'none', label: 'Color: None' }],
      feature: [{ value: 'none', label: 'Feature: None' }]
    },
    pants: {
      color: [{ value: 'none', label: 'Color: None' }],
      type: [{ value: 'none', label: 'Type: None' }]
    },
    shoes: {
      color: [{ value: 'none', label: 'Color: None' }],
      type: [{ value: 'none', label: 'Type: None' }]
    }
  };
}

/**
 * 将后端选项格式转换为前端使用的格式
 * @param {Object} backendOptions - 后端返回的选项数据
 * @returns {Object} 适合前端使用的选项格式
 */
// usePersonOptions.js 的 transformOptionsForUI 函数

function transformOptionsForUI(backendOptions) {
  // 初始化UI选项对象，包含'none'选项
  const uiOptions = getEmptyOptions();
  
  try {
    console.log('原始后端选项数据:', backendOptions);
    
    // 从后端数据中提取唯一的属性值
    if (backendOptions) {
      Object.keys(uiOptions).forEach(part => {
        if (backendOptions[part]) {
          Object.keys(uiOptions[part]).forEach(attr => {
            if (backendOptions[part][attr] && Array.isArray(backendOptions[part][attr])) {
              // 将后端选项添加到UI选项中
              backendOptions[part][attr].forEach(option => {
                // 确保不重复添加'none'选项
                if (option.value !== 'none') {
                  uiOptions[part][attr].push(option);
                }
              });
            }
          });
        }
      });
    }
    
    // 确保所有选项已经排序
    Object.keys(uiOptions).forEach(part => {
      Object.keys(uiOptions[part]).forEach(attr => {
        uiOptions[part][attr] = uiOptions[part][attr].sort((a, b) => 
          a.value === 'none' ? -1 : (b.value === 'none' ? 1 : a.label.localeCompare(b.label))
        );
      });
    });
    
    console.log('转换后的选项数据:', uiOptions);
  } catch (error) {
    console.error('转换选项时出错:', error);
  }
  
  return uiOptions;
}

// 辅助函数：首字母大写
function capitalizeFirstLetter(string) {
  if (!string) return '';
  return string.charAt(0).toUpperCase() + string.slice(1);
}